// Formularios rapidos (crear/editar) que se abren desde cualquier pantalla o desde el buscador.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useSyncExternalStore, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import * as crm from '../api/crm';
import type { Empresa, Oportunidad, Persona } from '../api/types';
import { useSession } from '../auth/session';
import { useEtapas, useMiembros, useTodasEmpresas } from '../data/queries';
import { Dialog } from './Dialog';
import type { Cita } from '../api/citas';
import { CitaForm } from './CitaForm';
import { HorasForm } from './HorasForm';
import { LlamadaFicha } from './LlamadaFicha';
import { OportunidadFicha } from './OportunidadFicha';
import { ImportarContactos } from './ImportarContactos';
import { ReglasCorreo } from './ReglasCorreo';
import { Redactor } from './Redactor';
import { SelectorFecha } from './SelectorFecha';
import { TareaFicha } from './TareaFicha';
import { toast } from './toasts';

export type DialogoPedido =
  | { tipo: 'persona'; persona?: Persona; empresaId?: string }
  | { tipo: 'importarContactos' }
  | { tipo: 'reglasCorreo' }
  | { tipo: 'empresa'; empresa?: Empresa }
  | { tipo: 'oportunidad'; oportunidad?: Oportunidad; empresaId?: string; contactoId?: string; etapa?: string; volverA?: DialogoPedido }
  | { tipo: 'tarea'; personaId?: string; empresaId?: string; oportunidadId?: string; nombre?: string; volverA?: DialogoPedido }
  | { tipo: 'verOportunidad'; id: string }
  | { tipo: 'verTarea'; id: string; volverA?: DialogoPedido }
  | { tipo: 'verLlamada'; id: string }
  | { tipo: 'cita'; cita?: Cita; inicio?: string; personaId?: string; personaNombre?: string; empresaId?: string }
  | { tipo: 'horas'; minutos?: number; proyectoId?: string; casoId?: string; descripcion?: string; faseId?: string; fecha?: string }
  | { tipo: 'correo'; para?: string; cc?: string; asunto?: string; texto?: string; desde?: string; borrador?: string }
  | null;

let actual: DialogoPedido = null;
const subs = new Set<() => void>();
export function abrirDialogo(d: DialogoPedido) {
  actual = d;
  subs.forEach((s) => s());
}
const useDialogo = () =>
  useSyncExternalStore(
    (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    () => actual,
  );

export function Dialogos() {
  const d = useDialogo();
  // Algunos formularios se abren desde una ficha (p. ej. "Nueva tarea" en una oportunidad): al terminar, se vuelve a ella.
  const cerrar = () => abrirDialogo(d && 'volverA' in d ? (d.volverA ?? null) : null);
  if (!d) return null;
  if (d.tipo === 'persona') return <PersonaForm persona={d.persona} empresaId={d.empresaId} onClose={cerrar} />;
  if (d.tipo === 'empresa') return <EmpresaForm empresa={d.empresa} onClose={cerrar} />;
  if (d.tipo === 'oportunidad') return <OportunidadForm oportunidad={d.oportunidad} empresaId={d.empresaId} contactoId={d.contactoId} etapa={d.etapa} onClose={cerrar} />;
  if (d.tipo === 'verTarea') return <TareaFicha id={d.id} onClose={cerrar} />;
  if (d.tipo === 'verOportunidad') return <OportunidadFicha id={d.id} onClose={cerrar} />;
  if (d.tipo === 'verLlamada') return <LlamadaFicha id={d.id} onClose={cerrar} />;
  if (d.tipo === 'cita') return <CitaForm cita={d.cita} inicio={d.inicio} personaId={d.personaId} personaNombre={d.personaNombre} empresaId={d.empresaId} onClose={cerrar} />;
  if (d.tipo === 'horas') return <HorasForm minutos={d.minutos} proyectoId={d.proyectoId} casoId={d.casoId} descripcion={d.descripcion} faseId={d.faseId} fecha={d.fecha} onClose={cerrar} />;
  if (d.tipo === 'reglasCorreo') return <ReglasCorreo onClose={cerrar} />;
  if (d.tipo === 'importarContactos') return <ImportarContactos onClose={cerrar} />;
  if (d.tipo === 'correo') return <Redactor para={d.para} cc={d.cc} asunto={d.asunto} texto={d.texto} desde={d.desde} borrador={d.borrador} onClose={cerrar} />;
  return <TareaForm personaId={d.personaId} empresaId={d.empresaId} oportunidadId={d.oportunidadId} nombre={d.nombre} onClose={cerrar} />;
}

// Eliminar en dos toques: el primero pide confirmar (sin ventanas del navegador).
function BotonEliminar({ que, onEliminar }: { que: string; onEliminar: () => Promise<void> }) {
  // Borrar contactos y empresas (D99).
  const puede = useSession().puede('clientes.borrar');
  const [confirmar, setConfirmar] = useState(false);
  const [borrando, setBorrando] = useState(false);
  if (!puede && (que === 'contacto' || que === 'empresa')) return null;
  return confirmar ? (
    <span style={{ display: 'inline-flex', gap: 6, marginRight: 'auto', alignItems: 'center' }}>
      <button
        type="button"
        className="btn bad"
        disabled={borrando}
        onClick={async () => {
          setBorrando(true);
          try {
            await onEliminar();
          } finally {
            setBorrando(false);
          }
        }}
      >
        {borrando ? 'Eliminando…' : `Sí, eliminar ${que}`}
      </button>
      <button type="button" className="btn ghost" onClick={() => setConfirmar(false)}>
        No
      </button>
    </span>
  ) : (
    <button type="button" className="btn ghost" style={{ marginRight: 'auto', color: 'var(--bad-ink)' }} onClick={() => setConfirmar(true)}>
      Eliminar
    </button>
  );
}

function useEnviar() {
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setError('');
    setEnviando(true);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  return { error, enviando, run };
}

function EmpresaSelect({ value, onChange, id }: { value: string; onChange: (v: string) => void; id: string }) {
  const empresas = useTodasEmpresas();
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Sin empresa</option>
      {(empresas.data ?? []).map((e) => (
        <option key={e.id} value={e.id}>
          {e.nombre}
        </option>
      ))}
    </select>
  );
}

function PersonaForm({ persona, empresaId, onClose }: { persona?: Persona; empresaId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [v, setV] = useState<crm.PersonaInput>({
    nombre: persona?.nombre ?? '',
    apellido: persona?.apellido ?? '',
    cargo: persona?.cargo ?? '',
    email: persona?.email ?? '',
    telefono: persona?.telefono ?? '',
    empresaId: persona?.empresaId ?? empresaId ?? null,
  });
  const { error, enviando, run } = useEnviar();
  const set = (k: keyof crm.PersonaInput) => (e: { target: { value: string } }) => setV((x) => ({ ...x, [k]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!v.nombre.trim()) return;
    void run(async () => {
      const p = persona ? await crm.editarPersona(persona.id, v) : await crm.crearPersona(v);
      void qc.invalidateQueries({ queryKey: ['personas'] });
      void qc.invalidateQueries({ queryKey: ['persona', p.id] });
      toast(persona ? 'Contacto actualizado.' : `Contacto creado: ${p.nombreCompleto}.`);
      onClose();
      if (!persona) nav(`/personas/${p.id}`);
    });
  };
  return (
    <Dialog titulo={persona ? 'Editar contacto' : 'Nuevo contacto'} subtitulo={persona ? persona.nombreCompleto : 'Solo el nombre es obligatorio.'} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <div className="grid2">
          <label>
            Nombre
            <input id="f-nombre" value={v.nombre} onChange={set('nombre')} required autoComplete="off" />
          </label>
          <label>
            Apellido
            <input id="f-apellido" value={v.apellido} onChange={set('apellido')} autoComplete="off" />
          </label>
        </div>
        <div className="grid2">
          <label>
            Teléfono
            <input id="f-telefono" value={v.telefono} onChange={set('telefono')} inputMode="tel" placeholder="+57 310 000 0000" autoComplete="off" />
          </label>
          <label>
            Correo
            <input id="f-email" type="email" value={v.email} onChange={set('email')} autoComplete="off" />
          </label>
        </div>
        <div className="grid2">
          <label>
            Cargo
            <input id="f-cargo" value={v.cargo} onChange={set('cargo')} autoComplete="off" />
          </label>
          <label>
            Empresa
            <EmpresaSelect id="f-empresa" value={v.empresaId ?? ''} onChange={(x) => setV((y) => ({ ...y, empresaId: x || null }))} />
          </label>
        </div>
        {error ? <p className="error">{error}</p> : null}
        <div className="actions">
          {persona ? (
            <BotonEliminar
              que="contacto"
              onEliminar={async () => {
                try {
                  await crm.borrarPersona(persona.id);
                  void qc.invalidateQueries({ queryKey: ['personas'] });
                  toast('Contacto eliminado. El administrador puede restaurarlo desde la papelera.');
                  onClose();
                  nav('/contactos');
                } catch (e) {
                  toast((e as Error).message, 'error');
                }
              }}
            />
          ) : null}
          <button type="button" className="btn ghost" onClick={onClose} data-close>
            Cancelar
          </button>
          <button className="btn primary" disabled={enviando || !v.nombre.trim()}>
            {enviando ? 'Guardando…' : persona ? 'Guardar cambios' : 'Crear contacto'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function EmpresaForm({ empresa, onClose }: { empresa?: Empresa; onClose: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [v, setV] = useState<crm.EmpresaInput>({ nombre: empresa?.nombre ?? '', dominio: empresa?.dominio ?? '', ciudad: empresa?.ciudad ?? '' });
  const { error, enviando, run } = useEnviar();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!v.nombre.trim()) return;
    void run(async () => {
      const x = empresa ? await crm.editarEmpresa(empresa.id, v) : await crm.crearEmpresa(v);
      void qc.invalidateQueries({ queryKey: ['empresas'] });
      void qc.invalidateQueries({ queryKey: ['empresa', x.id] });
      toast(empresa ? 'Empresa actualizada.' : `Empresa creada: ${x.nombre}.`);
      onClose();
      if (!empresa) nav(`/empresas/${x.id}`);
    });
  };
  return (
    <Dialog titulo={empresa ? 'Editar empresa' : 'Nueva empresa'} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <label>
          Nombre
          <input id="f-emp-nombre" value={v.nombre} onChange={(e) => setV({ ...v, nombre: e.target.value })} required autoComplete="off" />
        </label>
        <div className="grid2">
          <label>
            Sitio web
            <input id="f-emp-dominio" value={v.dominio} onChange={(e) => setV({ ...v, dominio: e.target.value })} placeholder="empresa.co" autoComplete="off" />
          </label>
          <label>
            Ciudad
            <input id="f-emp-ciudad" value={v.ciudad} onChange={(e) => setV({ ...v, ciudad: e.target.value })} autoComplete="off" />
          </label>
        </div>
        {error ? <p className="error">{error}</p> : null}
        <div className="actions">
          {empresa ? (
            <BotonEliminar
              que="empresa"
              onEliminar={async () => {
                try {
                  await crm.borrarEmpresa(empresa.id);
                  void qc.invalidateQueries({ queryKey: ['empresas'] });
                  toast('Empresa eliminada. El administrador puede restaurarlo desde la papelera.');
                  onClose();
                  nav('/empresas');
                } catch (e) {
                  toast((e as Error).message, 'error');
                }
              }}
            />
          ) : null}
          <button type="button" className="btn ghost" onClick={onClose} data-close>
            Cancelar
          </button>
          <button className="btn primary" disabled={enviando || !v.nombre.trim()}>
            {enviando ? 'Guardando…' : empresa ? 'Guardar cambios' : 'Crear empresa'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function OportunidadForm({ oportunidad: o, empresaId, contactoId, etapa, onClose }: { oportunidad?: Oportunidad; empresaId?: string; contactoId?: string; etapa?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const etapas = useEtapas();
  const [v, setV] = useState({
    nombre: o?.nombre ?? '',
    monto: o ? String(Math.round(o.monto)) : '',
    moneda: o?.moneda ?? 'COP',
    etapa: o?.etapa ?? etapa ?? '',
    empresaId: o?.empresaId ?? empresaId ?? '',
    cierre: o?.cierre ? o.cierre.slice(0, 10) : '',
    contactoId: o?.contactoId ?? contactoId ?? '',
    responsableId: o?.responsableId ?? '',
  });
  const miembros = useMiembros();
  // Contacto: las personas de la empresa elegida (y el contacto actual aunque sea de otra).
  const gente = useQuery({ queryKey: ['colegas', v.empresaId], queryFn: () => crm.personasDeEmpresa(v.empresaId), enabled: !!v.empresaId });
  const { error, enviando, run } = useEnviar();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const et = v.etapa || etapas.data?.[0]?.valor;
    if (!v.nombre.trim() || !et) return;
    void run(async () => {
      const datos: crm.OportunidadInput = {
        nombre: v.nombre,
        monto: Number(v.monto.replace(/[^\d]/g, '')) || 0,
        moneda: v.moneda,
        etapa: et,
        empresaId: v.empresaId || null,
        contactoId: v.contactoId || null,
        cierre: v.cierre ? new Date(`${v.cierre}T12:00:00`).toISOString() : null,
        responsableId: v.responsableId || null,
      };
      if (o) await crm.editarOportunidad(o.id, datos);
      else await crm.crearOportunidad(datos);
      void qc.invalidateQueries({ queryKey: ['oportunidades'] });
      void qc.invalidateQueries({ queryKey: ['oportunidad'] });
      toast(o ? 'Oportunidad actualizada.' : 'Oportunidad creada.');
      onClose();
    });
  };
  return (
    <Dialog titulo={o ? 'Oportunidad' : 'Nueva oportunidad'} subtitulo={o?.empresa ? o.empresa.nombre : undefined} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <label>
          Qué se vende
          <input id="f-op-nombre" value={v.nombre} onChange={(e) => setV({ ...v, nombre: e.target.value })} placeholder="Renovación 3CX · 20 usuarios" required autoComplete="off" />
        </label>
        <div className="grid2">
          <label>
            Valor
            <input id="f-op-monto" value={v.monto} onChange={(e) => setV({ ...v, monto: e.target.value })} inputMode="numeric" placeholder="18.500.000" autoComplete="off" />
          </label>
          <label>
            Moneda
            <select id="f-op-moneda" value={v.moneda} onChange={(e) => setV({ ...v, moneda: e.target.value })}>
              <option value="COP">COP</option>
              <option value="USD">USD</option>
            </select>
          </label>
        </div>
        <div className="grid2">
          <label>
            Etapa
            <select id="f-op-etapa" value={v.etapa || etapas.data?.[0]?.valor || ''} onChange={(e) => setV({ ...v, etapa: e.target.value })}>
              {(etapas.data ?? []).map((x) => (
                <option key={x.valor} value={x.valor}>
                  {x.etiqueta}
                </option>
              ))}
            </select>
          </label>
          <label>
            Cierre esperado
            <SelectorFecha id="f-op-cierre" value={v.cierre} onChange={(x) => setV({ ...v, cierre: x })} etiqueta="Cierre esperado" />
          </label>
        </div>
        <label>
          Empresa
          <EmpresaSelect id="f-op-empresa" value={v.empresaId} onChange={(x) => setV({ ...v, empresaId: x, contactoId: x === v.empresaId ? v.contactoId : '' })} />
        </label>
        <div className="grid2">
          <label>
            Contacto
            <select id="f-op-contacto" value={v.contactoId} onChange={(e) => setV({ ...v, contactoId: e.target.value })}>
              <option value="">{v.empresaId ? 'Sin contacto' : 'Elige primero la empresa'}</option>
              {o?.contacto && !(gente.data ?? []).some((p) => p.id === o.contacto!.id) ? <option value={o.contacto.id}>{o.contacto.nombre}</option> : null}
              {(gente.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombreCompleto}
                </option>
              ))}
            </select>
          </label>
          <label>
            Responsable
            <select id="f-op-responsable" value={v.responsableId} onChange={(e) => setV({ ...v, responsableId: e.target.value })}>
              <option value="">Sin responsable</option>
              {(miembros.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nombre}
                </option>
              ))}
            </select>
          </label>
        </div>
        {error ? <p className="error">{error}</p> : null}
        <div className="actions">
          {o ? (
            <BotonEliminar
              que="oportunidad"
              onEliminar={async () => {
                try {
                  await crm.borrarOportunidad(o.id);
                  void qc.invalidateQueries({ queryKey: ['oportunidades'] });
                  toast('Oportunidad eliminada. El administrador puede restaurarla desde la papelera.');
                  abrirDialogo(null); // no se vuelve a la ficha: ya no existe
                } catch (e) {
                  toast((e as Error).message, 'error');
                }
              }}
            />
          ) : null}
          {o?.empresaId ? (
            <button type="button" className="btn ghost" onClick={() => (onClose(), nav(`/empresas/${o.empresaId}`))}>
              Ver empresa
            </button>
          ) : null}
          <button type="button" className="btn ghost" onClick={onClose} data-close>
            Cancelar
          </button>
          <button className="btn primary" disabled={enviando || !v.nombre.trim()}>
            {enviando ? 'Guardando…' : o ? 'Guardar cambios' : 'Crear oportunidad'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// Fecha de mañana en la hora local (toISOString es UTC: en Colombia después de las 7 p. m. daba pasado mañana).
const mananaA9 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function TareaForm({ personaId, empresaId, oportunidadId, nombre, onClose }: { personaId?: string; empresaId?: string; oportunidadId?: string; nombre?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { yo } = useSession();
  const miembros = useMiembros();
  const [v, setV] = useState({ titulo: nombre ? `Llamar a ${nombre}` : '', cuerpo: '', fecha: mananaA9(), hora: '09:00', asignadoId: yo.miembroId ?? '' });
  const { error, enviando, run } = useEnviar();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!v.titulo.trim()) return;
    void run(async () => {
      await crm.crearTarea({
        titulo: v.titulo,
        vence: v.fecha ? new Date(`${v.fecha}T${v.hora || '09:00'}:00`).toISOString() : null,
        asignadoId: v.asignadoId || null,
        cuerpo: v.cuerpo.trim(),
        personaId,
        empresaId,
        oportunidadId,
      });
      void qc.invalidateQueries({ queryKey: ['tareas'] });
      void qc.invalidateQueries({ queryKey: ['timeline'] });
      toast('Tarea creada.');
      onClose();
    });
  };
  return (
    <Dialog titulo="Nueva tarea" subtitulo={nombre ? `Para ${nombre}` : undefined} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <label>
          Qué hay que hacer
          <input id="f-t-titulo" value={v.titulo} onChange={(e) => setV({ ...v, titulo: e.target.value })} required autoComplete="off" />
        </label>
        <label>
          Descripción (opcional)
          <textarea id="f-t-cuerpo" value={v.cuerpo} onChange={(e) => setV({ ...v, cuerpo: e.target.value })} rows={3} placeholder="Detalles o pasos" />
        </label>
        <div className="grid2">
          <label>
            Día
            <SelectorFecha id="f-t-fecha" value={v.fecha} onChange={(x) => setV({ ...v, fecha: x })} etiqueta="Fecha límite" data-baliza="fecha" />
          </label>
          <label>
            Hora
            <input id="f-t-hora" type="time" value={v.hora} onChange={(e) => setV({ ...v, hora: e.target.value })} />
          </label>
        </div>
        <label>
          Responsable
          <select id="f-t-responsable" value={v.asignadoId} onChange={(e) => setV({ ...v, asignadoId: e.target.value })}>
            <option value="">Sin asignar</option>
            {(miembros.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.id === yo.miembroId ? `${m.nombre} (yo)` : m.nombre}
              </option>
            ))}
          </select>
        </label>
        {error ? <p className="error">{error}</p> : null}
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose} data-close>
            Cancelar
          </button>
          <button className="btn primary" disabled={enviando || !v.titulo.trim()}>
            {enviando ? 'Guardando…' : 'Crear tarea'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
